/**
 * "Combined chapter PPTX" — generates a single .pptx covering every
 * lesson in the chapter (chapter cover slide + module divider per
 * lesson + each lesson's slides). Sits next to "PPTX for all lessons"
 * which produces one deck PER lesson.
 *
 * UX:
 *  - Idle  → small outline Button that says "Combined PPTX"
 *  - Running → spinner + status label sourced from the AI Jobs Tray
 *  - Done   → download icon link + tiny "Regenerate" affordance
 *
 * The actual progress card lives in the global AI Jobs Tray (bottom
 * right) so the user can navigate away and come back; this button only
 * mirrors the local state for at-a-glance feedback on the chapter row.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import { Button } from "@/components/ui/button";
import { FileStack, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function CombinedChapterPptxButton({ chapter, testIdSuffix }) {
    const { trackJob, jobs: trayJobs } = useAiJobs();
    const [busy, setBusy] = useState(false);
    const [combined, setCombined] = useState({
        url: chapter?.combined_pptx_url || "",
        slideCount: chapter?.combined_pptx_slide_count || 0,
        lessonCount: chapter?.combined_pptx_lesson_count || 0,
    });

    // Sync prop -> local whenever the chapter doc refreshes.
    useEffect(() => {
        setCombined({
            url: chapter?.combined_pptx_url || "",
            slideCount: chapter?.combined_pptx_slide_count || 0,
            lessonCount: chapter?.combined_pptx_lesson_count || 0,
        });
    }, [chapter?.combined_pptx_url, chapter?.combined_pptx_slide_count, chapter?.combined_pptx_lesson_count]);

    // Is there a chapter_pptx job running for this chapter right now?
    const liveJob = Object.values(trayJobs || {}).find(
        (j) =>
            j.kind === "chapter_pptx" &&
            j.chapterId === chapter?.id &&
            (j.status === "running" || j.status === "queued"),
    );

    const tid = (s) => `combined-pptx-${s}-${chapter?.id || "x"}${testIdSuffix ? `-${testIdSuffix}` : ""}`;

    const handleGenerate = async () => {
        if (!chapter?.id) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/chapters/${chapter.id}/pptx/generate-combined`);
            trackJob({
                kind: "chapter_pptx",
                jobId: data.job_id,
                chapterId: chapter.id,
                chapterTitle: data.chapter_title || chapter.title || "Chapter",
                numLessons: data.lesson_count || 0,
                total: 10,
                onDone: (jobData) => {
                    setCombined({
                        url: jobData?.pptx_url || "",
                        slideCount: jobData?.pptx_slide_count || 0,
                        lessonCount: jobData?.lesson_count || 0,
                    });
                    // Notify the parent CurriculumBuilder so other chapter
                    // rows can refresh if needed.
                    try {
                        window.dispatchEvent(new CustomEvent("cco:chapter-combined-pptx-generated", {
                            detail: { chapterId: chapter.id, pptxUrl: jobData?.pptx_url },
                        }));
                    } catch { /* no-op */ }
                },
            });
            toast.success("Combined chapter deck queued for AI.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start combined PPTX.");
        } finally {
            setBusy(false);
        }
    };

    // Live (running)
    if (liveJob) {
        const stage = liveJob.stage || "queued";
        const stageText =
            stage === "planning" ? "AI planning…"
            : stage === "rendering" ? "Building deck…"
            : stage === "uploading" ? "Uploading…"
            : "Queued…";
        return (
            <Button
                size="sm"
                variant="outline"
                disabled
                className="gap-1.5 border-amber-400 text-amber-700"
                data-testid={tid("running")}
                title={stageText}
            >
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="text-xs font-semibold">{stageText}</span>
            </Button>
        );
    }

    // Ready (already generated)
    if (combined.url) {
        return (
            <div className="inline-flex items-center gap-1">
                <a href={combined.url} target="_blank" rel="noopener noreferrer">
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-primary text-primary hover:bg-primary/10"
                        data-testid={tid("download")}
                        title={`Download combined deck — ${combined.lessonCount} lessons, ${combined.slideCount} slides`}
                    >
                        <Download className="h-3.5 w-3.5" />
                        <span className="text-xs font-semibold">
                            Combined PPTX
                            {combined.slideCount ? (
                                <span className="opacity-70 font-normal">
                                    {" "}· {combined.slideCount} slides
                                </span>
                            ) : null}
                        </span>
                    </Button>
                </a>
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={handleGenerate}
                    disabled={busy}
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    data-testid={tid("regen")}
                    title="Regenerate combined deck"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                </Button>
            </div>
        );
    }

    // Idle
    return (
        <Button
            size="sm"
            variant="outline"
            onClick={handleGenerate}
            disabled={busy}
            className="gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
            data-testid={tid("idle")}
            title="Generate a single PPTX that covers the entire chapter (one deck combining all lessons with section dividers)."
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileStack className="h-3.5 w-3.5" />}
            <span className="text-xs font-semibold">Combined PPTX</span>
        </Button>
    );
}
